import type { ICryptoAdapter, IBackupEncryptionAdapter } from '@app/domain/ports';
import { BackupEncryptionError } from './errors';

/**
 * Backup encryption adapter — pure TypeScript, framework-agnostic.
 *
 * Encrypts/decrypts backup files using PGP symmetric encryption.
 * Passphrases (DEK hex + master password) are supplied via constructor callback
 * so this class has zero dependency on app-layer services (DekService, stores).
 */
export class BackupEncryptionAdapter implements IBackupEncryptionAdapter {
  constructor(
    private readonly crypto: ICryptoAdapter,
    /** Returns [dekPassphrase, masterPassword] — called fresh on each operation. */
    private readonly getPassphrases: () => Promise<string[]>,
  ) {}

  async encrypt(data: Uint8Array): Promise<Uint8Array> {
    try {
      const passwords = await this.getPassphrases();
      return await this.crypto.encryptWithPgp(data, passwords);
    } catch (error) {
      throw new BackupEncryptionError('encrypt', 'Failed to encrypt backup.', error);
    }
  }

  async decrypt(data: Uint8Array, extraPasswords?: string[]): Promise<Uint8Array> {
    try {
      const passphrases = await this.getPassphrases();
      const passwords = [...passphrases, ...(extraPasswords ?? [])];
      return await this.crypto.decryptWithPgp(data, passwords);
    } catch (error) {
      throw new BackupEncryptionError(
        'decrypt',
        'Failed to decrypt backup. Invalid password or corrupted file.',
        error,
      );
    }
  }
}
